import { CloseIcon } from 'assets/icons/CloseIcon'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { CheckChip } from 'components/CheckBox/CheckChip'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { ISelectValue } from 'constants/globalTypes'
import React, { useEffect, useState } from 'react'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { IngredientsList } from './Food.styled'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import {
  clearSingleDish,
  createDish,
  editDish,
  getFoodDietaryTags,
  getSingleDish,
} from 'features/food/foodSlice'
import { standardFieldValidation } from 'utils/validationUtils'
import useModalClose from 'hooks/useModalClose'
import { XIcon } from 'assets/icons/XIcon'
import { IDishesModalState } from 'types/food.interface'
import {
  FoodDishSourceType,
  ICreateKitchenDishRequest,
  IUpdateKitchenDishRequest,
} from 'types/api/foodApi.interface'

const sourceTypeOptions = [
  { label: 'Prepared', value: FoodDishSourceType.Prepared },
  { label: 'Purchased', value: FoodDishSourceType.Purchased },
]

const parseInventoryItems = (data: any) =>
  (data?.data || []).map((i: any) => ({ label: i.name, value: i.id }))

const parseSuppliers = (data: any) =>
  (data?.data || []).map((i: any) => ({ label: i.name, value: i.id }))

const parseIngredients = (data: any) =>
  (data?.data || [])
    .filter((i: any) => i.active !== false)
    .map((i: any) => ({
      label: i.name,
      value: i.id,
      unit: i.recipeUnit?.name,
    }))

const toRecipeLines = (recipeLines: IDishesModalState['recipeLines']) =>
  recipeLines.map((l) => ({
    ingredientId: Number(l.ingredient.value),
    amount: Number(l.amount),
  }))

export const DishesModal = ({ closeModal, id }: { closeModal: any; id?: number }) => {
  const [state, setState] = useState<IDishesModalState>({
    name: '',
    sourceType: FoodDishSourceType.Prepared,
    dietaryTagCodes: [],
    supplier: null,
    dishSku: null,
    active: false,
    recipeLines: [],
  })
  const [addingLine, setAddingLine] = useState<{
    ingredient: (ISelectValue & { unit?: string }) | null
    amount: number | null
  }>({
    ingredient: null,
    amount: null,
  })
  const [invalidFields, setInvalidFields] = useState(id ? [] : ['name', 'dietaryTagCodes'])
  const dispatch = useDispatch<AppDispatch>()
  const { singleDish } = useSelector((store: RootState) => store.food.dishes)
  const { dietaryTags } = useSelector((store: RootState) => store.food)

  useFocusFirstInput()
  useModalClose(closeModal)

  const isPurchased = state.sourceType === FoodDishSourceType.Purchased
  const isPrepared = state.sourceType === FoodDishSourceType.Prepared

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    standardFieldValidation(e, setInvalidFields)
    setState((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const toggleDietaryTag = (code: string) => {
    setState((prev) => {
      const dietaryTagCodes = prev.dietaryTagCodes.includes(code)
        ? prev.dietaryTagCodes.filter((c) => c !== code)
        : [...prev.dietaryTagCodes, code]
      setInvalidFields((fields) => {
        const without = fields.filter((f) => f !== 'dietaryTagCodes')
        return dietaryTagCodes.length ? without : [...without, 'dietaryTagCodes']
      })
      return { ...prev, dietaryTagCodes }
    })
  }

  const handleAddLine = () => {
    if (!addingLine.ingredient || !(addingLine.amount > 0)) return
    setState((prev) => ({
      ...prev,
      recipeLines: [
        ...prev.recipeLines,
        { ingredient: addingLine.ingredient, amount: addingLine.amount },
      ],
    }))
    setAddingLine({ ingredient: null, amount: null })
  }

  const handleRemoveLine = (label: string) => {
    setState((prev) => ({
      ...prev,
      recipeLines: prev.recipeLines.filter((l) => l.ingredient.label !== label),
    }))
  }

  const recipeChanged = () => {
    const latest = singleDish?.latestNormativeVersion
    if (!latest) return state.recipeLines.length > 0
    const current = state.recipeLines
      .map((l) => `${l.ingredient.value}:${l.amount}`)
      .sort()
      .join('|')
    const saved = latest.lines
      .map((l) => `${l.ingredient?.id}:${l.amount}`)
      .sort()
      .join('|')
    return current !== saved
  }

  const handleSave = async () => {
    if (id) {
      const body: IUpdateKitchenDishRequest = {
        name: state.name,
        sourceType: state.sourceType,
        dietaryTagCodes: state.dietaryTagCodes,
        supplierId: isPurchased ? state.supplier?.value || null : null,
        dishSkuId: state.dishSku?.value || null,
        active: state.active,
      }
      if (isPrepared && recipeChanged() && state.recipeLines.length) {
        body.lines = toRecipeLines(state.recipeLines)
      }
      const resp = await dispatch(editDish({ id, body }))
      if (resp.meta.requestStatus !== 'fulfilled') return
    } else {
      const body: ICreateKitchenDishRequest = {
        name: state.name,
        sourceType: state.sourceType,
        dietaryTagCodes: state.dietaryTagCodes,
        supplierId: isPurchased ? state.supplier?.value || null : null,
        dishSkuId: state.dishSku?.value || null,
        activate: state.active,
      }
      if (isPrepared && state.recipeLines.length) {
        body.lines = toRecipeLines(state.recipeLines)
      }
      const resp = await dispatch(createDish(body))
      if (resp.meta.requestStatus !== 'fulfilled') return
    }
    closeModal()
  }

  useEffect(() => {
    dispatch(getFoodDietaryTags({ page: 1, limit: 200 }))
    if (id) {
      dispatch(getSingleDish(id))
    }
    return () => {
      dispatch(clearSingleDish())
    }
  }, [id])

  useEffect(() => {
    if (singleDish) {
      setState({
        id: singleDish.id,
        name: singleDish.name,
        sourceType: singleDish.sourceType,
        dietaryTagCodes: (singleDish.dietaryTags || []).map((t) => t.code),
        supplier: singleDish.supplier
          ? { label: singleDish.supplier.name, value: singleDish.supplier.id }
          : null,
        dishSku: singleDish.dishSku
          ? { label: singleDish.dishSku.name, value: singleDish.dishSku.id }
          : null,
        active: singleDish.active,
        recipeLines: (singleDish.latestNormativeVersion?.lines || [])
          .filter((l) => l.ingredient)
          .map((l) => ({
            ingredient: {
              label: l.ingredient.name,
              value: l.ingredient.id,
              unit: l.ingredient.recipeUnit?.name,
            },
            amount: l.amount,
          })),
      })
      setInvalidFields((fields) => {
        const without = fields.filter((f) => f !== 'dietaryTagCodes')
        return singleDish.dietaryTags?.length ? without : [...without, 'dietaryTagCodes']
      })
    }
  }, [singleDish])

  const saveDisabled =
    !!invalidFields.length ||
    (isPrepared && state.active && !state.recipeLines.length) ||
    (isPurchased && state.active && (!state.supplier || !state.dishSku))

  return (
    <Modal black>
      <Flex column gap='1rem'>
        <Flex between width='100%' center>
          <TitleText weight='bold'>{id ? 'Edit' : 'Add'} Dish</TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <Flex width='100%' gap='1rem'>
          <Flex width='100%' column>
            <Input
              labelText='Dish name'
              name='name'
              placeholder='Potato soup, grilled meat...'
              value={state.name}
              onChange={handleChange}
              invalid={invalidFields.includes('name') ? 'true' : ''}
              style={{ minWidth: '400px' }}
            />
            <HonestSelectInput
              labelText='Source Type'
              options={sourceTypeOptions}
              defaultValue={state.sourceType}
              onChange={(v: ISelectValue) =>
                setState((prev) => ({
                  ...prev,
                  sourceType: Number(v.value),
                  recipeLines:
                    Number(v.value) === FoodDishSourceType.Purchased ? [] : prev.recipeLines,
                  supplier: Number(v.value) === FoodDishSourceType.Prepared ? null : prev.supplier,
                }))
              }
              name='sourceType'
            />
            <Flex column gap='0.5rem' style={{ marginBottom: '1rem' }}>
              <BodyText>Dietary Tags</BodyText>
              <Flex gap='0.5rem' wrap>
                {(dietaryTags.data || []).map((tag) => (
                  <CheckChip
                    key={tag.code}
                    text={tag.name || tag.code}
                    active={state.dietaryTagCodes.includes(tag.code)}
                    onClick={() => toggleDietaryTag(tag.code)}
                  />
                ))}
              </Flex>
            </Flex>
            {isPurchased && (
              <HonestSelectAsyncInput
                link='inventory-suppliers'
                name='supplier'
                labelText='Supplier'
                placeholder='Supplier name'
                defaultValue={state?.supplier?.label}
                onChange={(v: ISelectValue) => setState((prev) => ({ ...prev, supplier: v }))}
                invalid={!state.supplier}
                parserFunction={parseSuppliers}
              />
            )}
            <HonestSelectAsyncInput
              link='inventory/item'
              name='dishSku'
              labelText={
                isPurchased
                  ? 'Dish SKU (inventory item)'
                  : 'Dish SKU (optional, produce-to-stock)'
              }
              placeholder='Inventory item...'
              defaultValue={state?.dishSku?.label}
              onChange={(v: ISelectValue) => setState((prev) => ({ ...prev, dishSku: v }))}
              invalid={isPurchased && state.active && !state.dishSku}
              parserFunction={parseInventoryItems}
              nomarg
            />
          </Flex>
          {isPrepared && (
            <Flex width='100%' column gap='1rem'>
              <BodyText>Recipe (per portion)</BodyText>
              <HonestSelectAsyncInput
                link='kitchen/management/ingredient'
                name='ingredient'
                labelText='Ingredient'
                placeholder='Milk, sugar...'
                defaultValue={addingLine?.ingredient?.label}
                onChange={(v: ISelectValue & { unit?: string }) =>
                  setAddingLine((prev) => ({ ...prev, ingredient: v }))
                }
                parserFunction={parseIngredients}
                width='400px'
              />
              <Flex gap='1rem' alignEnd>
                <Input
                  name='amount'
                  width='120px'
                  labelText='Amount'
                  placeholder='1, 2, 2.547...'
                  value={addingLine.amount}
                  type='number'
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setAddingLine((prev) => ({ ...prev, amount: e.target.valueAsNumber }))
                  }
                  nomarg
                />
                <OtherText grey style={{ paddingBottom: '0.75rem', minWidth: '3rem' }}>
                  {addingLine.ingredient?.unit || '—'}
                </OtherText>
                <Button
                  variant='primary'
                  style={{ width: '120px' }}
                  disabled={!addingLine.amount || !addingLine.ingredient}
                  onClick={handleAddLine}
                >
                  Add
                </Button>
              </Flex>
              <IngredientsList>
                <BodyText>
                  {state.recipeLines.map((l) => (
                    <Flex gap='1rem' key={l.ingredient.value}>
                      {l.amount + ' ' + (l.ingredient.unit || '') + ' ' + l.ingredient.label}
                      <CursorPointer onClick={() => handleRemoveLine(l.ingredient.label)}>
                        <XIcon />
                      </CursorPointer>
                    </Flex>
                  ))}
                </BodyText>
              </IngredientsList>
            </Flex>
          )}
        </Flex>
        <Flex width='100%' between center>
          <Button
            variant={state.active ? 'primary' : 'secondary'}
            onClick={() => setState((prev) => ({ ...prev, active: !prev.active }))}
          >
            Active: {state.active ? 'Yes' : 'No'}
          </Button>
          <Button variant='primary' disabled={saveDisabled} onClick={handleSave}>
            Save
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
